// client/src/App.tsx
import { useEffect, Suspense } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { TranslationProvider } from '@/contexts/TranslationContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { SocketProvider } from '@/contexts/SocketContext';
import { SetupProvider } from '@/contexts/SetupContext';
import { WalkthroughProvider } from '@/contexts/WalkthroughContext';
import AppRoutes from './routes';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useChunkRecoveryNotifications } from '@/hooks/useChunkRecoveryNotifications';
import { OfflineFallback, useOfflineState } from '@/components/OfflineFallback';
import { reportApiErrorFromUnknown } from '@/lib/utils/sentry-api-errors';

const queryCache = new QueryCache({
  onError: (error, query) => {
    reportApiErrorFromUnknown(error, {
      source: 'react_query',
      query_key: JSON.stringify(query.queryKey),
    });
  },
});

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    reportApiErrorFromUnknown(error, {
      source: 'react_query_mutation',
      mutation_key: mutation.options.mutationKey
        ? JSON.stringify(mutation.options.mutationKey)
        : undefined,
    });
  },
});

// Configure QueryClient with better defaults for mobile and session handling
const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Reduce stale time to ensure fresh data after returning to app
      staleTime: 2 * 60 * 1000, // 2 minutes
      // Increase cache time to prevent white screens
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry auth errors
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Retry network errors up to 2 times
        return failureCount < 2;
      },
      // Refetch on window focus to get fresh data when returning to app
      refetchOnWindowFocus: true,
      // Refetch when reconnecting to network
      refetchOnReconnect: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: 'always'
    },
    mutations: {
      // Retry failed mutations once
      retry: 1
    }
  }
});

// Store settings and user preferences are mounted by dozens of components, so
// 'always' refetched them on every page open. They're config that rarely
// changes, so let them honor staleTime like a normal query.
queryClient.setQueryDefaults(['settings'], { refetchOnMount: true });
queryClient.setQueryDefaults(['userPreferences'], { refetchOnMount: true });

// Component to handle service worker navigation messages
function ServiceWorkerNavigationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for messages from the service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NAVIGATE_TO') {
        // Navigate to the requested URL
        navigate(event.data.url);

        // If it's a chat notification, we might want to focus on the specific group and message
        if (event.data.notificationData?.groupId) {
          // Store the group ID in sessionStorage so the chat page can auto-select it
          sessionStorage.setItem('focusGroupId', event.data.notificationData.groupId);

          // Clear it after a short delay to prevent it from affecting future navigation
          setTimeout(() => {
            sessionStorage.removeItem('focusGroupId');
          }, 5000);
        }
      } else if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
        // Show a brief message about the update
        if (event.data.message) {
          // Update message handled by service worker
        }

        // Store update notification for user to see
        sessionStorage.setItem('appUpdateAvailable', JSON.stringify({
          version: event.data.version,
          message: event.data.message,
          timestamp: Date.now()
        }));

        // Dispatch event that UI can listen for to show update notification
        window.dispatchEvent(new CustomEvent('appUpdateAvailable', {
          detail: event.data
        }));
      } else if (event.data && event.data.type === 'FORCE_RELOAD') {
        // Legacy handler for emergency reloads only
        window.location.reload();
      } else if (event.data && (event.data.type === 'STALE_ASSET_DETECTED' || event.data.type === 'STALE_ASSETS_DETECTED')) {
        // Stale assets detected - app was likely updated while user was idle
        console.warn('[ServiceWorker] Stale assets detected, reloading app:', event.data.reason || event.data.assetUrl);
        
        // Clear service worker caches if multiple assets are stale (full deployment)
        if (event.data.type === 'STALE_ASSETS_DETECTED') {
          // Clear all caches to ensure fresh load
          if ('caches' in window) {
            caches.keys().then(cacheNames => {
              return Promise.all(cacheNames.map(name => caches.delete(name)));
            }).catch(err => {
              console.error('[ServiceWorker] Error clearing caches:', err);
            });
          }
        }
        
        // Reload after a brief delay to allow cache clearing
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };

    // Register the message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Cleanup
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [navigate]);

  return null; // This component doesn't render anything
}

// Loading fallback for Suspense
function SuspenseFallback() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-4 text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  )
}

// Inner component that uses hooks (must be inside providers)
function AppContent() {
  useChunkRecoveryNotifications();
  const isOffline = useOfflineState();

  return (
    <>
      <OfflineFallback isOffline={isOffline} isLoading={false} />
      <AppRoutes />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ServiceWorkerNavigationHandler />
          <AuthProvider>
            <TranslationProvider>
              <NotificationProvider>
                <SubscriptionProvider>
                  <SetupProvider>
                    <WalkthroughProvider>
                      <SocketProvider>
                        <Suspense fallback={<SuspenseFallback />}>
                          <AppContent />
                        </Suspense>
                      </SocketProvider>
                    </WalkthroughProvider>
                  </SetupProvider>
                </SubscriptionProvider>
              </NotificationProvider>
            </TranslationProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;

// client/src/routes/index.tsx
